import { Trans } from "@lingui/macro";
import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import { UiLink, UiSpinner } from "@smp/ui";
import clsx from "clsx";
import { FC, useMemo, useState } from "react";
import { InView } from "react-intersection-observer";
import { useUpdateEffect } from "react-use";

import {
  CreateButton,
  TemplateCard,
  UnsplashCard,
} from "@/components/features/polotno_templates/components";
import { useContextPolotnoTemplates } from "@/components/features/polotno_templates/context";
import { useTemplateVirtualizer } from "@/components/features/polotno_templates/hooks";

import classes from "./virtual_grid.module.css";
import { IVirtualGridProps } from "./virtual_grid.types";

const UNSPLASH_HEAD_HEIGHT = 70;

export const VirtualGrid: FC<IVirtualGridProps> = ({
  unsplashEntries,
  templates,
  inViewTemplateLoader,
  hasTemplatesNextPage,
}) => {
  const [parentRef, setParentRef] = useState<HTMLDivElement | null>(null);
  const [totalHeight, setTotalHeight] = useState(0);

  const {
    state: {
      isWidget,
      selectedTags,
      sortBy,
      searchString,
      virtualizerTemplatesOptions,
    },
    refs,
  } = useContextPolotnoTemplates();

  const templateItems = useMemo(() => {
    const results: Parameters<typeof useTemplateVirtualizer>[0] = [];

    if (templates.length) {
      if (!isWidget) {
        results.push({ key: "create-btn", type: "create-btn" });
      }

      templates.forEach((template) => {
        results.push({
          key: `template-${template.id}`,
          payload: template,
          type: "template",
        });
      });
    }

    if (inViewTemplateLoader) {
      results.push({ key: "loader", type: "loader" });
    }

    return results;
  }, [inViewTemplateLoader, isWidget, templates]);

  const unsplashItems = useMemo(() => {
    const results: Parameters<typeof useTemplateVirtualizer>[0] = [];

    if (unsplashEntries.length) {
      if (!templateItems.length) {
        results.push({ key: "create-btn", type: "create-btn" });
      }

      unsplashEntries.forEach((entry) => {
        results.push({
          key: `unsplash-${entry.id}`,
          payload: entry,
          type: "unsplash",
        });
      });
    }

    return results;
  }, [templateItems.length, unsplashEntries]);

  const {
    virtualizer: virtualizerTemplates,
    calculateStyles,
    getTotalSizeCustom: getTotalSizeCustomTemplates,
  } = useTemplateVirtualizer(templateItems, parentRef, {
    initialMeasurementsCache: virtualizerTemplatesOptions?.measurementsCache,
    lanes: virtualizerTemplatesOptions?.lanes,
  });

  const virtualizerTemplatesHeight = getTotalSizeCustomTemplates();

  const isUnsplashListVisible = useMemo(() => {
    if (hasTemplatesNextPage) {
      return false;
    }

    return (
      !virtualizerTemplates.scrollElement ||
      virtualizerTemplates.scrollElement?.clientHeight +
        (virtualizerTemplates?.scrollOffset || 0) >=
        virtualizerTemplatesHeight
    );
  }, [
    hasTemplatesNextPage,
    virtualizerTemplates.scrollElement,
    virtualizerTemplates?.scrollOffset,
    virtualizerTemplatesHeight,
  ]);

  const {
    virtualizer: virtualizerUnsplash,
    getTotalSizeCustom: getTotalSizeCustomUnsplash,
  } = useTemplateVirtualizer(unsplashItems, parentRef, {
    scrollMargin: virtualizerTemplatesHeight + UNSPLASH_HEAD_HEIGHT,
    lanes: virtualizerTemplatesOptions?.lanes,
  });

  const virtualizerUnsplashHeight = Math.max(getTotalSizeCustomUnsplash(), 0);

  useIsomorphicLayoutEffect(() => {
    if (refs?.virtualizerTemplates && virtualizerTemplates.scrollElement) {
      refs.virtualizerTemplates(virtualizerTemplates);
    }
  }, [refs, refs?.virtualizerTemplates, virtualizerTemplates]);

  useUpdateEffect(() => {
    setTotalHeight(
      Math.max(virtualizerUnsplashHeight, virtualizerTemplatesHeight),
    );
  }, [virtualizerTemplatesHeight, virtualizerUnsplashHeight]);

  useUpdateEffect(() => {
    virtualizerTemplates.scrollToOffset(0);
    virtualizerUnsplash.scrollToOffset(0);
  }, [selectedTags, sortBy, searchString]);

  return (
    <div
      className={clsx(classes.container, isWidget && classes.container_widget)}
      ref={setParentRef}
    >
      <div
        className={clsx(
          classes.scroller,
          import.meta.env.SSR && classes.scroller_ssr,
        )}
        style={{
          height: !import.meta.env.SSR ? `${totalHeight}px` : undefined,
        }}
      >
        {(parentRef || import.meta.env.SSR) && (
          <>
            {virtualizerTemplates.getVirtualItems().map((virtualItem) => {
              const item = templateItems[virtualItem.index];
              const style = calculateStyles(virtualItem);

              return (
                <div style={style} key={virtualItem.key.toString()}>
                  <div
                    ref={virtualizerTemplates.measureElement}
                    data-index={virtualItem.index}
                  >
                    {item.type === "loader" && (
                      <InView
                        onChange={(inView) =>
                          inView &&
                          inViewTemplateLoader &&
                          inViewTemplateLoader()
                        }
                      >
                        <div className={classes.loader}>
                          <UiSpinner size={24} />
                        </div>
                      </InView>
                    )}

                    {item.type === "create-btn" && <CreateButton />}

                    {item.type === "template" && (
                      <TemplateCard template={item.payload} />
                    )}
                  </div>
                </div>
              );
            })}

            {!!unsplashItems.length && isUnsplashListVisible && (
              <>
                <div
                  className={classes.unsplashHead}
                  style={{
                    transform: `translate(0px, ${virtualizerTemplatesHeight}px)`,
                  }}
                >
                  <Trans>
                    Photo by{" "}
                    <UiLink target="_blank" href="https://unsplash.com">
                      Unsplash
                    </UiLink>
                  </Trans>
                </div>

                {virtualizerUnsplash.getVirtualItems().map((virtualItem) => {
                  const item = unsplashItems[virtualItem.index];

                  return (
                    <div
                      style={calculateStyles(virtualItem)}
                      key={virtualItem.key.toString()}
                    >
                      <div
                        className={classes.card}
                        ref={virtualizerUnsplash.measureElement}
                        data-index={virtualItem.index}
                        data-key={virtualItem.key}
                      >
                        {item.type === "create-btn" && <CreateButton />}

                        {item.type === "unsplash" && (
                          <UnsplashCard payload={item.payload} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
